const { getLocation } = require('../../tools')

// Shape rows from `location.pgAdapter.getByFsidsWithOptions` — a location row
// with its `data_location_options` columns joined on — into location entities,
// so a read over the agave crud/pg channel answers in the same shape as the
// couch read it stands in for.
//
// Running the rows through the same `getLocation` the couch path uses (via
// `docToEntity`) is what keeps the two interchangeable: `location`, the names,
// and the `additionalData` normalisation all stay in one place rather than being
// re-derived here. This file only rebuilds the couch *doc* the hydrator expects.
//
// Every column read here is inverted from the write side of that same column,
// so the round trip is faithful by construction rather than by guesswork: the
// `data_location_options` columns from `LocationOptionsPgAdapter.upsert`, and
// the two `data_location` columns — `address` and `supply_chain_level` — from
// the location field maps (`tools/fields-map-psm.js` and `tools/fields-map.js`,
// which agree on both).
//
// NOT recoverable from these rows, and absent from the result:
//
// - `programs` / `services` / `configurations`. The couch path derives them from
//   `doc.programs` — a time-series of service assignments — filtered by the
//   report date and the caller's funder/route scope. Postgres keeps that in
//   `data_location_service`, which this read does not join, so the hydrator sees
//   no programs and answers `[]` for all three. This is why only `getByIds`
//   reads over this channel and `get` does not: every `getByIds` caller was
//   checked and reads identity and `additionalData` only, whereas `get` hands a
//   whole location to callers that do read `services` — the PSM delivery
//   statement among them (see `LocationApi.get` for the audit). Joining
//   `data_location_service` here is what lets `get` follow.
// - `supplierRelationships`, which is derived from the configuration docs of a
//   shelflife service, and `programsHistory`, which is `doc.programs` itself.
// - `additionalData.uuid`. `data_location.uuid` looks like the column for it,
//   but the adapter is built with `addUuid: true`, so a row can carry a uuid
//   Postgres minted that was never on the couch doc. Answering with it would
//   invent an identifier rather than return one.
// - `_rev`, and anything else the couch doc carries that never got a column.
const LAT_LNG_SEPARATOR = ','

// `data_location.lat` / `.long` are written by `parseCoordinates`, which splits
// `additionalData.latLng` on a comma. Reversing it keeps the string form the map
// and the PSM field maps read. Both halves are required: a row holding only one
// is the partial entry bulk import rejects, and half a coordinate is worse than
// none.
const rowToLatLng = row => (
  row.lat === null || row.lat === undefined || row.long === null || row.long === undefined
    ? undefined
    : `${row.lat}${LAT_LNG_SEPARATOR}${row.long}`
)

// Only keys the row actually carries a value for, so a location whose option row
// is missing entirely looks the same as it does out of couch — the key absent
// rather than present and null.
const defined = entries => entries.reduce((additionalData, [key, value]) => (
  value === null || value === undefined ? additionalData : { ...additionalData, [key]: value }
), {})

// `physicalAddress` is the odd one out twice over: it lives on the location row
// as `address` rather than in `data_location_options`, and it is the one key
// `defined` cannot keep off the result — the hydrator's
// `getNormalizedAdditionalData` always sets it, so a location with no address
// answers undefined rather than missing, on this path and the couch one alike.
// Both field maps write the column from `additionalData.physicalAddress`, and
// every statement header — delivery, sales, inventory — reads it back as that,
// so it belongs here with the option columns rather than left to the caller.
const rowToAdditionalData = row => defined([
  ['warehouseCode', row.warehouse_code],
  ['facilityId', row.facility_id],
  ['ward', row.ward],
  ['hasPcr', row.has_pcr],
  ['tracksPartnerBalances', row.tracks_partner_balances],
  ['suppliesColdChain', row.supplies_cold_chain],
  ['physicalAddress', row.address],
  ['latLng', rowToLatLng(row)]
])

// `supply_chain_level`, not `level`. The couch `level` — 'sdp', 'pack-point',
// 'supplier' — is written to `supply_chain_level` by both field maps.
// `data_location.level` is a different field entirely: the SL map fills it from
// `additionalData.level`, and the PSM couch-to-pg transform hardcodes it to '5'
// for every row, so reading it here would answer '5' as the supply chain level
// for every PSM location — and `getShipmentConfirmEligibility`, `isSDPShipment`
// and friends all branch on that.
const rowToDoc = row => ({
  _id: row.fsid,
  name: row.name_of_pharmacy || '',
  fullName: row.name_of_pharmacy || '',
  level: row.supply_chain_level,
  additionalData: rowToAdditionalData(row),
  ...(row.alias ? { alias: row.alias } : {}),
  ...(row.batch_tracking_services ? { batchTrackingServices: row.batch_tracking_services } : {})
})

// Rows with no fsid are dropped rather than hydrated: the fsid *is* the couch
// `_id` every caller keys the result by, and `getLocation` derives the geo
// `location` object from it, so a row without one cannot answer for any id that
// was asked for.
const pgRowsToLocations = (rows = [], { date } = {}) =>
  rows
    .filter(row => row && row.fsid)
    .map(row => getLocation(rowToDoc(row), date))

module.exports = { pgRowsToLocations, rowToDoc, rowToAdditionalData }
